/**
 * BatchUploadZone - Zone de drop multi-fichiers pour OCR batch
 *
 * Design System: Dashboard context (text-gray-900, bg-white/70, teal-600)
 * Responsive: mobile-first
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  FileText,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui-v2';

interface BatchUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  isProcessing: boolean;
  progress: number;
  total: number;
}

const MAX_FILES = 20;
const MAX_SIZE = 10 * 1024 * 1024; // 10 Mo
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

export function BatchUploadZone({
  onFilesSelected,
  isProcessing,
  progress,
  total,
}: BatchUploadZoneProps) {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback((files: FileList | File[]): { valid: File[]; errors: string[] } => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const fileErrors: string[] = [];

    if (fileArray.length > MAX_FILES) {
      fileErrors.push(`Maximum ${MAX_FILES} fichiers. Vous en avez sélectionné ${fileArray.length}.`);
      return { valid: [], errors: fileErrors };
    }

    for (const file of fileArray) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        fileErrors.push(`"${file.name}" : format non supporté. Acceptés : PDF, PNG, JPG`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        fileErrors.push(`"${file.name}" : trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo, max 10 Mo)`);
        continue;
      }
      validFiles.push(file);
    }

    return { valid: validFiles, errors: fileErrors };
  }, []);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const { valid, errors: fileErrors } = validateFiles(files);
    setErrors(fileErrors);
    if (valid.length > 0) {
      setSelectedFiles(valid);
    }
  }, [validateFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  }, [selectedFiles, onFilesSelected]);

  // Processing view
  if (isProcessing) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl p-8">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('expenses.batch.extracting', 'Extraction en cours...')}
            </h3>
            <p className="text-gray-500 mt-1">
              {t('expenses.batch.progress', '{{progress}}/{{total}} fichiers traités', { progress, total })}
            </p>
          </div>
          <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
            <div
              className="bg-teal-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${total > 0 ? (progress / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-3xl p-8 sm:p-12 text-center cursor-pointer
          transition-all duration-300
          ${isDragOver
            ? 'border-teal-500 bg-teal-50/50'
            : 'border-gray-300/50 bg-white/10 hover:border-teal-400 hover:bg-white/20'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleInputChange}
          className="hidden"
        />
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragOver ? 'text-teal-600' : 'text-gray-400'}`} />
        <h3 className="text-lg font-medium text-gray-900">
          {t('expenses.batch.dropTitle', 'Glissez vos factures ici')}
        </h3>
        <p className="text-sm text-gray-500 mt-2">
          {t('expenses.batch.dropSubtitle', 'PDF, PNG ou JPG — jusqu\'à {{max}} fichiers, 10 Mo max chacun', { max: MAX_FILES })}
        </p>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50/80 border border-red-200 rounded-2xl p-4 space-y-1">
          {errors.map((error, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl overflow-hidden">
          <div className="p-4 border-b border-white/20">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-teal-600" />
                {t('expenses.batch.filesSelected', '{{count}} fichier(s) sélectionné(s)', { count: selectedFiles.length })}
              </h3>
              <Button
                variant="secondary"
                onClick={handleSubmit}
                icon={<Upload className="w-4 h-4" />}
              >
                {t('expenses.batch.startExtraction', 'Lancer l\'extraction OCR')}
              </Button>
            </div>
          </div>

          <div className="divide-y divide-white/20">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between px-4 py-3 hover:bg-white/10">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} Ko</p>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
